import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createSubscriptionSetupIntent, fetchSubscriptionCard } from "@/api/tier";
import type { OrgCard } from "@/interfaces/tier";

/**
 * The ORGANIZATION's card for the subscription — read, and the SetupIntent that collects one.
 *
 * Deliberately NOT `useCampaignPaymentMethod`, which returns a GROUP's card. A subscription is
 * billed to the organization's Stripe Customer, and Stripe refuses a PaymentMethod attached to a
 * different Customer, so a group card cannot pay for it however convenient it looks on screen.
 *
 * The read goes to Stripe on the server rather than a mirrored table: it is one card fetched when
 * a drawer opens, and mirroring would only create a way for the card shown to differ from the card
 * charged.
 */
export function useSubscriptionCard(enabled = true) {
  const query = useQuery<OrgCard | null>({
    queryKey: ["tier", "payment-method"],
    queryFn: fetchSubscriptionCard,
    enabled,
    // A card just added through the SetupIntent must not be served from a stale cache — the drawer
    // refetches immediately after confirming one.
    staleTime: 0,
  });

  return {
    card: query.data ?? null,
    isLoading: query.isLoading,
    refetch: query.refetch,
  };
}

/** Mints a fresh SetupIntent against the organization's Customer. */
export function useSubscriptionSetupIntent() {
  return useMutation({ mutationFn: () => createSubscriptionSetupIntent() });
}

/** Invalidates the org card after one is saved. */
export function useInvalidateSubscriptionCard() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ["tier", "payment-method"] });
}
